import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Car, Plus, Search, RefreshCcw } from "lucide-react";

type Cliente = {
  id: string;
  nome: string;
  tipo?: string | null;
  documento?: string | null;
  telefone?: string | null;
  email?: string | null;
  cidade?: string | null;
  uf?: string | null;
  createdAt?: string;
};

type Meta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  q: string;
};

export default function ClientesList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const qParam = searchParams.get("q") ?? "";
  const pageParam = Number(searchParams.get("page") ?? "1");
  const limitParam = Number(searchParams.get("limit") ?? "20");

  const [q, setQ] = useState(qParam);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Cliente[]>([]);
  const [meta, setMeta] = useState<Meta>({
    page: pageParam,
    limit: limitParam,
    total: 0,
    totalPages: 1,
    q: qParam,
  });

  const canPrev = useMemo(() => meta.page > 1, [meta.page]);
  const canNext = useMemo(() => meta.page < meta.totalPages, [meta.page, meta.totalPages]);

  function updateUrl(next: { q?: string; page?: number; limit?: number }) {
    const newParams = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      if (next.q.trim()) newParams.set("q", next.q.trim());
      else newParams.delete("q");
    }

    if (next.page !== undefined) newParams.set("page", String(next.page));
    if (next.limit !== undefined) newParams.set("limit", String(next.limit));

    // defaults bonitos
    if (newParams.get("page") === "1") newParams.delete("page");
    if (newParams.get("limit") === "20") newParams.delete("limit");

    setSearchParams(newParams);
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/clientes", {
        params: {
          q: qParam || undefined,
          page: pageParam || 1,
          limit: limitParam || 20,
        },
      });

      setRows(data?.data ?? []);
      setMeta(data?.meta ?? { page: 1, limit: 20, total: 0, totalPages: 1, q: "" });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Erro ao carregar clientes";
      toast.error(msg);
      setRows([]);
      setMeta((m) => ({ ...m, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }

  // Carrega sempre que mudar URL (q/page/limit)
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, pageParam, limitParam]);

  function onSearch() {
    updateUrl({ q, page: 1 });
  }

  function formatDoc(doc?: string | null) {
    if (!doc) return "-";
    return doc;
  }

  function formatLocal(cidade?: string | null, uf?: string | null) {
    if (!cidade && !uf) return "-";
    if (cidade && uf) return `${cidade}/${uf}`;
    return cidade || uf || "-";
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
            <p className="text-sm text-slate-500">
              {meta.total ? `${meta.total} cliente(s) encontrado(s)` : "Gerencie seus clientes"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => load()}
              className="gap-2"
              disabled={loading}
            >
              <RefreshCcw size={16} />
              Atualizar
            </Button>

            <Link to="/clientes/novo">
              <Button className="gap-2">
                <Plus size={16} />
                Novo Cliente
              </Button>
            </Link>
          </div>
        </div>

        {/* BUSCA */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg w-full">
              <Search size={16} className="text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearch();
                }}
                placeholder="Buscar por nome, CPF/CNPJ, telefone, e-mail..."
                className="bg-transparent outline-none text-sm w-full"
              />
            </div>

            <Button onClick={onSearch} disabled={loading} className="whitespace-nowrap">
              Buscar
            </Button>
          </div>

          {/* chips rápidos */}
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setQ("");
                updateUrl({ q: "", page: 1 });
              }}
              disabled={loading && rows.length === 0}
            >
              Limpar
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">Por página:</span>
              <select
                value={limitParam}
                onChange={(e) => updateUrl({ limit: Number(e.target.value), page: 1 })}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABELA */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Documento</th>
                  <th className="text-left px-4 py-3">Telefone</th>
                  <th className="text-left px-4 py-3">Local</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                      Carregando clientes...
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                      Nenhum cliente encontrado.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{c.nome}</div>
                        <div className="text-xs text-slate-500">{c.email || ""}</div>
                      </td>

                      <td className="px-4 py-3">{formatDoc(c.documento)}</td>
                      <td className="px-4 py-3">{c.telefone || "-"}</td>
                      <td className="px-4 py-3">{formatLocal(c.cidade, c.uf)}</td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Link to={`/clientes/${c.id}/editar`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <Pencil size={16} />
                              Editar
                            </Button>
                          </Link>

                          <Link to={`/clientes/${c.id}/veiculos`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <Car size={16} />
                              Veículos
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* PAGINAÇÃO */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
            <p className="text-xs text-slate-500">
              Página <span className="font-medium text-slate-800">{meta.page}</span> de{" "}
              <span className="font-medium text-slate-800">{meta.totalPages}</span>
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => updateUrl({ page: meta.page - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => updateUrl({ page: meta.page + 1 })}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
